/// <amd-module name="VSSUI/Components/Menu/Menu" />
import * as React from "react";
import { ObservableCollection, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { Callout } from "VSSUI/Callout";
import { Checkbox } from "VSSUI/Checkbox";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZone, FocusZoneContext, FocusZoneDirection, FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { Icon, IconSize } from "VSSUI/Icon";
import { IListItemDetails, List } from "VSSUI/List";
import { IMouseWithinEvents, MouseWithin } from "VSSUI/MouseWithin";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, getSafeIdSelector, isArrowKey, KeyCode, preventDefault, setFocusVisible } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import {
    IContextualMenuProps,
    IMenu,
    IMenuGroup,
    IMenuItem,
    IMenuItemDetails,
    IMenuItemProps,
    IMenuProps,
    MenuCell,
    MenuItemType
} from "./Menu.Props";

export interface IMenuState {
    expandedIndex: ObservableValue<number>;
}

interface IGroupInfo extends IMenuGroup {
    rank: number; // make rank required
    items: IMenuItem[]; // keep a list of items in the group
}

/**
 * Arrange the items into groups and put separators between them and headings above them as needed.
 *
 * @param items Menu items with optional order and groupKey properties
 * @param groupInfo Optional list of menu groups
 */
export function groupMenuItems(items: IMenuItem[], groupInfo?: IMenuGroup[]): IMenuItem[] {
    const groupMap: { [id: string]: IGroupInfo } = {};
    let maxGroupRank = 0;
    const ungroupedItems: IMenuItem[] = [];
    const groups: IMenuGroup[] = groupInfo || [];

    // gather known groups
    if (groups.length > 0) {
        maxGroupRank = groups.reduce((max, g) => (g.rank || 0 > max ? g.rank : max), 0) || 0;
        for (const g of groups) {
            groupMap[g.key] = {
                key: g.key,
                rank: g.rank === undefined ? ++maxGroupRank : g.rank,
                items: []
            };
        }
    }

    // put all menu items in groups
    for (const i of items) {
        if (i.groupKey) {
            if (groupMap[i.groupKey]) {
                groupMap[i.groupKey].items.push(i);
            } else {
                groupMap[i.groupKey] = {
                    key: i.groupKey,
                    rank: ++maxGroupRank,
                    items: [i]
                };
            }
        } else {
            ungroupedItems.push(i);
        }
    }

    // sort the groups
    const groupList = Object.keys(groupMap).map(n => groupMap[n]);
    groupList.sort((a, b) => (a.rank || Number.MAX_VALUE) - (b.rank || Number.MAX_VALUE));

    // add ungrouped items to end of group list
    groupList.push({
        key: "ungrouped",
        rank: ++maxGroupRank,
        items: ungroupedItems
    });

    // remove dividers from the beginning and end of each group
    groupList.forEach(g => {
        const array = g.items;
        while (array.length > 0 && array[0].itemType === MenuItemType.Divider) {
            array.shift();
        }
        while (array.length > 0 && array[array.length - 1].itemType === MenuItemType.Divider) {
            array.pop();
        }
    });

    // merge the groups into the final array
    items = [];
    let first = true;
    for (const g of groupList) {
        if (g.items.length === 0) {
            continue;
        }

        // add the separator or header for the top of the group
        if (!first) {
            items.push({
                id: `divider_${g.key}`,
                itemType: MenuItemType.Divider
            });
        }

        if (first) {
            first = false;
        }

        items = items.concat(g.items);
    }

    return items;
}

class MenuItemProvider extends ArrayItemProvider<IMenuItem> {
    private positions: number[] = [];
    private count: number | undefined;

    constructor(menuItems: IMenuItem[], menuGroups?: IMenuGroup[]) {
        super(menuItems);

        let derivedItems: IMenuItem[] = [];

        // Process the set of menu items.
        if (menuItems) {
            let shouldGroupMenuItems = false;
            let shouldSortMenuItems = false;
            let lastItemType: MenuItemType = MenuItemType.Divider;
            let dividerItem: IMenuItem | undefined;

            for (const menuItem of menuItems) {
                // Exclude hidden items
                if (menuItem.hidden) {
                    continue;
                }
                // Don't allow multiple dividers to render next to each other.
                if (menuItem.itemType === MenuItemType.Divider) {
                    if (menuItem.itemType === lastItemType) {
                        continue;
                    }

                    dividerItem = menuItem;
                } else {
                    if (dividerItem) {
                        derivedItems.push(dividerItem);
                        dividerItem = undefined;
                    }

                    derivedItems.push(menuItem);
                }

                lastItemType = menuItem.itemType || MenuItemType.Normal;

                // If the item is ranked or grouped we need to sort and group them.
                shouldGroupMenuItems = !!menuItem.groupKey || shouldGroupMenuItems;
                shouldSortMenuItems = menuItem.rank! >= 0 || shouldSortMenuItems;
            }

            if (shouldSortMenuItems) {
                derivedItems.sort((a, b) => {
                    const aRank = a.rank || Number.MAX_VALUE;
                    const bRank = b.rank || Number.MAX_VALUE;
                    return aRank - bRank;
                });
            }

            if (shouldGroupMenuItems) {
                derivedItems = groupMenuItems(derivedItems, menuGroups);
            }
        }

        // Update the items to be the derived items.
        this.items = derivedItems;
    }

    // Custom getCount to support excluding the decorative items from the count.
    public getCount(): number {
        if (this.count === undefined) {
            this.count = 0;
            for (const menuItem of this.items) {
                if (menuItem.itemType === MenuItemType.Divider || menuItem.itemType === MenuItemType.Header) {
                    this.positions.push(-1);
                } else {
                    this.positions.push(++this.count);
                }
            }
        }

        return this.count;
    }

    public getItem(index: number): IMenuItem {
        return this.items[index];
    }

    public getPosition(index: number): number {
        if (!this.positions.length) {
            this.getCount();
        }
        return this.positions[index];
    }
}

export class Menu extends React.Component<IMenuProps, IMenuState> implements IMenu {
    private containerElement = React.createRef<HTMLDivElement>();
    private itemProvider: MenuItemProvider;

    public constructor(props: Readonly<IMenuProps>) {
        super(props);
        this.state = {
            expandedIndex: new ObservableValue<number>(-1)
        };
    }

    public render(): JSX.Element {
        return (
            <Observer items={this.props.items}>
                {(props: { items: IMenuItem[] }) => {
                    this.itemProvider = new MenuItemProvider(props.items, this.props.groups);
                    return this.renderList();
                }}
            </Observer>
        );
    }

    public expandItem = (menuItem: IMenuItem | null, expanded: boolean) => {
        if (!menuItem && this.state.expandedIndex.value !== -1) {
            menuItem = this.itemProvider.getItem(this.state.expandedIndex.value);
        }

        if (menuItem && menuItem.subMenuProps) {
            for (let index = 0; index < this.itemProvider.length; index++) {
                if (menuItem === this.itemProvider.getItem(index)) {
                    if (expanded) {
                        this.state.expandedIndex.value = index;
                    } else {
                        this.state.expandedIndex.value = -1;
                    }
                    break;
                }
            }
        }
    };

    public focus = () => {
        if (this.containerElement.current) {
            this.containerElement.current.focus();
        }
    };

    public getParent = () => {
        return this.props.parentMenu;
    };

    public onActivate = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        if (this.props.onActivate) {
            this.props.onActivate(menuItem, event);
        }
    };

    private renderList(): JSX.Element {
        return (
            <div className="bolt-menu-container no-outline" ref={this.containerElement} tabIndex={-1}>
                {this.itemProvider.length > 0 && (
                    <React.Fragment>
                        <div className="bolt-menu-spacer" onMouseDown={preventDefault} />
                        <List
                            ariaLabel={this.props.ariaLabel}
                            className={css(this.props.className, "bolt-menu")}
                            columnCount={7}
                            focuszoneProps={null}
                            id={this.props.id}
                            itemProvider={this.itemProvider}
                            renderRow={this.renderMenuItem}
                            role="menu"
                            virtualize={false}
                        />
                        <div className="bolt-menu-spacer" onMouseDown={preventDefault} />
                    </React.Fragment>
                )}
            </div>
        );
    }

    private renderMenuItem = (index: number, menuItem: IMenuItem, details: IListItemDetails<IMenuItem>): JSX.Element => {
        const { onFocusItem } = details;

        const menuItemDetails: IMenuItemDetails = {
            expandedIndex: this.state.expandedIndex,
            menu: this,
            menuProps: this.props,
            onActivate: this.onActivate,
            onFocusItem: onFocusItem,
            position: this.itemProvider.getPosition(index),
            setSize: this.itemProvider.getCount()
        };

        if (menuItem.renderMenuItem) {
            return menuItem.renderMenuItem(index, menuItem, menuItemDetails);
        }

        const key = menuItem.id;

        switch (menuItem.itemType) {
            case MenuItemType.Divider:
                return MenuDivider(index, menuItem);

            case MenuItemType.Header:
                return MenuHeader(index, menuItem);

            default:
                return <MenuItem key={key} index={index} menuItem={menuItem} details={menuItemDetails} />;
        }
    };
}

export function MenuDivider(index: number, menuItem: IMenuItem): JSX.Element {
    return (
        <tr
            aria-hidden="true"
            className={css(menuItem.className, "bolt-menuitem-row bolt-list-row bolt-menuitem-divider")}
            key={menuItem.id || "divider-" + index}
            onMouseDown={preventDefault}
        >
            <td className="bolt-menuitem-cell bolt-list-cell" />
            <td className="bolt-menuitem-cell bolt-list-cell bolt-menuitem-divider-column" colSpan={5}>
                <div className="bolt-menuitem-divider-content" />
            </td>
            <td className="bolt-menuitem-cell bolt-list-cell" />
        </tr>
    );
}

export function MenuHeader(index: number, menuItem: IMenuItem): JSX.Element {
    return (
        <tr
            aria-level={1}
            className={css(menuItem.className, "bolt-menuitem-row bolt-list-row bolt-menuitem-header")}
            key={menuItem.id || "header-" + index}
            onMouseDown={preventDefault}
            role="heading"
        >
            <td className="bolt-menuitem-cell bolt-list-cell" />
            <td className="bolt-menuitem-cell bolt-list-cell" colSpan={3}>
                <div className="bolt-menuitem-cell-content bolt-menuitem-cell-text">{menuItem.text}</div>
            </td>
            <td className="bolt-menuitem-cell bolt-list-cell" colSpan={3} />
        </tr>
    );
}

export class MenuItem extends React.Component<IMenuItemProps> {
    private localKeyStroke: boolean = false;
    private expanded: boolean = false;
    private element = React.createRef<HTMLAnchorElement | HTMLTableRowElement>();

    public render(): JSX.Element {
        const { index, menuItem, details } = this.props;
        const { menu, position, setSize } = details;
        const { ariaLabel, checked, className, disabled, href, iconProps, readonly, secondaryText, subMenuProps, target } = menuItem;
        let { id, rel, text } = menuItem;

        // If this is a link menu item we will use an anchor otherwise a plain div.
        const CellType = href ? "div" : "td";
        const RowType = href ? "a" : "tr";

        // If the menu item is a link is targetting an external window or tab and no explicit rel
        // attribute was supplied we will set noopener.
        if (href && target && !rel) {
            rel = "noopener";
        }

        return (
            <Observer checked={checked} expandedIndex={{ observableValue: this.props.details.expandedIndex, filter: this.onExpandedChange }}>
                {(props: { checked?: boolean; expandedIndex: number }): JSX.Element => {
                    this.expanded = props.expandedIndex === index;

                    return (
                        <MouseWithin enterDelay={250} leaveDelay={250} onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
                            {(mouseWithinEvents: IMouseWithinEvents) => (
                                <FocusZoneContext.Consumer>
                                    {rowContext => (
                                        <FocusWithin onFocus={this.onFocus}>
                                            {(focusStatus: IFocusWithinStatus) => (
                                                <FocusZone direction={FocusZoneDirection.Horizontal}>
                                                    <RowType
                                                        aria-label={ariaLabel}
                                                        aria-checked={props.checked === true || undefined}
                                                        aria-controls={this.expanded && subMenuProps ? getSafeId(subMenuProps.id) : undefined}
                                                        aria-disabled={disabled ? "true" : undefined}
                                                        aria-expanded={subMenuProps ? this.expanded : undefined}
                                                        aria-haspopup={subMenuProps ? true : undefined}
                                                        aria-posinset={position}
                                                        aria-setsize={setSize}
                                                        className={css(
                                                            className,
                                                            "bolt-menuitem-row bolt-list-row bolt-menuitem-row-normal cursor-pointer",
                                                            disabled && "disabled",
                                                            this.expanded && "expanded",
                                                            focusStatus.hasFocus && "focused"
                                                        )}
                                                        data-focuszone={disabled ? undefined : rowContext.focuszoneId}
                                                        href={href}
                                                        id={getSafeId(id)}
                                                        role={props.checked !== undefined ? "menuitemcheckbox" : "menuitem"}
                                                        onBlur={focusStatus.onBlur}
                                                        onClick={this.onClick}
                                                        onFocus={focusStatus.onFocus}
                                                        onKeyDown={this.onKeyDown}
                                                        onKeyUp={this.onKeyUp}
                                                        onMouseDown={this.onMouseDown}
                                                        onMouseEnter={mouseWithinEvents.onMouseEnter}
                                                        onMouseLeave={mouseWithinEvents.onMouseLeave}
                                                        ref={this.element as any}
                                                        rel={rel}
                                                        tabIndex={disabled ? undefined : -1}
                                                        target={target}
                                                    >
                                                        <CellType className="bolt-menuitem-cell bolt-list-cell">
                                                            <div className="bolt-menuitem-cell-content flex-row" />
                                                        </CellType>
                                                        <CellType className="bolt-menuitem-cell bolt-list-cell">
                                                            {props.checked !== undefined &&
                                                                ((menuItem.renderMenuCell &&
                                                                    menuItem.renderMenuCell(MenuCell.State, menuItem, details)) || (
                                                                    <div className="bolt-menuitem-cell-content bolt-menuitem-cell-state flex-row">
                                                                        {readonly === true ? (
                                                                            Icon({
                                                                                className: css(!props.checked && "invisible"),
                                                                                iconName: "CheckMark"
                                                                            })
                                                                        ) : (
                                                                            <Checkbox
                                                                                checked={props.checked}
                                                                                disabled={disabled}
                                                                                excludeFocusZone={true}
                                                                                excludeTabStop={true}
                                                                                onChange={this.onClick}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                ))}
                                                        </CellType>
                                                        <CellType className="bolt-menuitem-cell bolt-list-cell">
                                                            {(menuItem.renderMenuCell && menuItem.renderMenuCell(MenuCell.Icon, menuItem, details)) ||
                                                                (iconProps && (
                                                                    <div className="bolt-menuitem-cell-content bolt-menuitem-cell-icon flex-row">
                                                                        {Icon(iconProps)}
                                                                    </div>
                                                                ))}
                                                        </CellType>
                                                        <CellType className="bolt-menuitem-cell bolt-list-cell">
                                                            {(menuItem.renderMenuCell &&
                                                                menuItem.renderMenuCell(MenuCell.PrimaryText, menuItem, details)) || (
                                                                <div
                                                                    id={getSafeId(id + "-text")}
                                                                    className="bolt-menuitem-cell-content bolt-menuitem-cell-text flex-row"
                                                                >
                                                                    {text ? (                                                                        
                                                                        <> {text} </>
                                                                    ) : (
                                                                        <div>&nbsp;</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </CellType>
                                                        <CellType className="bolt-menuitem-cell bolt-list-cell">
                                                            {(menuItem.renderMenuCell &&
                                                                menuItem.renderMenuCell(MenuCell.SecondaryText, menuItem, details)) ||
                                                                (secondaryText && (
                                                                    <div className="bolt-menuitem-cell-content bolt-menuitem-cell-secondary flex-row">
                                                                        {secondaryText}
                                                                    </div>
                                                                ))}
                                                        </CellType>
                                                        <CellType className="bolt-menuitem-cell bolt-list-cell">
                                                            {(menuItem.renderMenuCell &&
                                                                menuItem.renderMenuCell(MenuCell.Action, menuItem, details)) ||
                                                                (subMenuProps && (
                                                                    <div className="bolt-menuitem-cell-content bolt-menuitem-cell-submenu flex-row">
                                                                        {Icon({ iconName: "ChevronRightMed", size: IconSize.small })}
                                                                        {this.expanded && this.element.current && (
                                                                            <ContextualMenu
                                                                                anchorElement={this.element.current}
                                                                                anchorOffset={{ horizontal: 0, vertical: -8 }}
                                                                                anchorOrigin={{ horizontal: Location.end, vertical: Location.start }}
                                                                                subMenu={true}
                                                                                menuOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                                                                menuProps={subMenuProps}
                                                                                onActivate={this.props.details.onActivate}
                                                                                onDismiss={this.onDismissSubMenu}
                                                                                parentMenu={menu}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                ))}
                                                        </CellType>
                                                        <CellType className="bolt-menuitem-cell bolt-list-cell">
                                                            <div className="bolt-menuitem-cell-content flex-row" />
                                                        </CellType>
                                                    </RowType>
                                                </FocusZone>
                                            )}
                                        </FocusWithin>
                                    )}
                                </FocusZoneContext.Consumer>
                            )}
                        </MouseWithin>
                    );
                }}
            </Observer>
        );
    }

    private handleClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const menuItem = this.props.menuItem;

        if (menuItem.disabled) {
            event.preventDefault();
        } else if (!this.expanded) {
            let ownerResponse: boolean | void;

            // If the menu owner supplied a handler, we will get feedback from them before doing
            // default processing on the menu item.
            if (menuItem.onActivate) {
                ownerResponse = menuItem.onActivate(menuItem, event);
            }

            // If the owner specifically returned true, we will not perform any defaults.
            if (!ownerResponse) {
                if (!menuItem.href) {
                    event.preventDefault();
                }

                // For menus with sub-menus we will expand it on activation. For other menu items
                // they are executed.
                if (menuItem.subMenuProps) {
                    this.props.details.menu.expandItem(menuItem, true);
                } else if (menuItem.href) {
                    this.props.details.onActivate(menuItem, event);
                } else if (menuItem.checked === undefined || menuItem.readonly) {
                    this.props.details.onActivate(menuItem, event);
                }
            }
        }
    };

    // If the click handler doesn't return false explicitly close dismiss the menu.
    private onClick = (event: React.MouseEvent<HTMLElement>): void => {
        if (!event.defaultPrevented) {
            this.handleClick(event);
        }
    };

    private onDismissSubMenu = (dismissAll: boolean) => {
        if (!dismissAll && this.element.current) {
            this.props.details.menu.expandItem(this.props.menuItem, false);
        }
    };

    private onExpandedChange = (expandedIndex: number) => {
        return (this.expanded && expandedIndex !== this.props.index) || (!this.expanded && expandedIndex === this.props.index);
    };

    private onFocus = (event: React.FocusEvent<HTMLElement>): void => {
        if (this.element.current === document.activeElement) {
            this.props.details.onFocusItem(this.props.index, event);
        }
    };

    // Handle the keydown to expand the menu.
    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        this.localKeyStroke = true;

        if (!event.defaultPrevented) {
            const menuItem = this.props.menuItem;

            if (event.which === KeyCode.tab || event.which === KeyCode.space) {
                event.preventDefault();
            } else if (event.which === KeyCode.rightArrow && menuItem.subMenuProps) {
                event.preventDefault();
                this.props.details.menu.expandItem(menuItem, true);
            }
        }
    };

    // Translate the space and enter keys into onClick event for a menuItem.
    private onKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
        // If we get focus while a key is down we will get the keyup. We dont want
        // to process this key, it needs to originate from us.
        if (!this.localKeyStroke) {
            return;
        }

        if (!event.defaultPrevented) {
            if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                this.handleClick(event);
            }
        }
    };

    private onMouseDown = (event: React.MouseEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            const menuItem = this.props.menuItem;

            if (menuItem.disabled || this.props.details.expandedIndex.value === this.props.index) {
                event.preventDefault();
            }
        }
    };

    // If you hover over a menu with a submenu we will open it after a short delay
    // or stop the closing timeout.
    private onMouseEnter = () => {
        if (!this.props.menuItem.disabled) {
            if (this.element.current) {
                this.element.current.focus();
            }
            this.props.details.menu.expandItem(this.props.menuItem, true);
            setFocusVisible(false);
        }
    };

    // If you leave the menu item and sub-menu we will close the menu after a short delay
    // or stop the open timeout.
    private onMouseLeave = () => {
        this.onDismissSubMenu(false);
    };
}

export class ContextualMenu extends React.Component<IContextualMenuProps> {
    private calloutRef = React.createRef<Callout>();

    public render(): JSX.Element {
        let defaultActiveElement = ".bolt-menu-container";

        // Determine which element should be the first to get focus.
        // Headers may be the first row and they wont take focus.
        let items = ObservableLike.getValue(this.props.menuProps.items);
        // Need slice() because order of elements matters in ObservableCollection
        if (this.props.menuProps.items instanceof ObservableCollection) {
            items = items.slice();
        }
        const sortedItems = items.sort((a: IMenuItem, b: IMenuItem) => {
            return (a.rank || Number.MAX_VALUE) - (b.rank || Number.MAX_VALUE);
        });
        for (let menuIndex = 0; menuIndex < sortedItems.length; menuIndex++) {
            if (sortedItems[menuIndex].itemType === MenuItemType.Normal || sortedItems[menuIndex].itemType === undefined) {
                const menuItemId = sortedItems[menuIndex].id;
                if (!menuItemId || sortedItems[menuIndex].disabled) {
                    continue;
                }

                defaultActiveElement = getSafeIdSelector(menuItemId);
                break;
            }
        }

        return (
            <Observer
                menuItems={{
                    observableValue: this.props.menuProps.items,
                    filter: () => {
                        this.calloutRef.current?.updateLayout();
                        return false;
                    }
                }}
            >
                {() => (
                    <Callout
                        ref={this.calloutRef}
                        anchorElement={this.props.anchorElement}
                        anchorOffset={this.props.anchorOffset}
                        anchorOrigin={this.props.anchorOrigin}
                        anchorPoint={this.props.anchorPoint}
                        blurDismiss={true}
                        calloutOrigin={this.props.menuOrigin}
                        className={this.props.className}
                        contentClassName={css(
                            "bolt-contextual-menu flex-column custom-scrollbar depth-8",
                            this.props.subMenu && "bolt-contextual-submenu"
                        )}
                        contentShadow={true}
                        onDismiss={this.onDismiss}
                        fixedLayout={this.props.fixedLayout}
                        focuszoneProps={{
                            defaultActiveElement: defaultActiveElement,
                            direction: FocusZoneDirection.Vertical,
                            focusOnMount: true,
                            preprocessKeyStroke: this.preprocessKeyStroke,
                            circularNavigation: true
                        }}
                        id={this.props.menuProps.id + "-callout"}
                        portalProps={{ className: "bolt-menu-portal" }}
                    >
                        <div className="bolt-contextualmenu-container" onKeyDown={this.onKeyDown}>
                            <Menu {...this.props.menuProps} onActivate={this.onActivate} parentMenu={this.props.parentMenu} />
                        </div>
                    </Callout>
                )}
            </Observer>
        );
    }

    private onDismiss = () => {
        if (this.props.onDismiss) {
            this.props.onDismiss(false);
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.escape || event.which === KeyCode.tab || (event.which === KeyCode.leftArrow && this.props.subMenu)) {
                event.preventDefault();
                if (this.props.onDismiss) {
                    this.props.onDismiss(false);
                }
            }
        }
    };

    private onActivate = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (this.props.menuProps.onActivate) {
            this.props.menuProps.onActivate(menuItem, event);
        }

        if (this.props.onActivate) {
            this.props.onActivate(menuItem, event);
        }

        if (this.props.onDismiss) {
            this.props.onDismiss(true);
        }
    };

    private preprocessKeyStroke = (event: React.KeyboardEvent<HTMLElement>): FocusZoneKeyStroke => {
        if (isArrowKey(event)) {
            return FocusZoneKeyStroke.IgnoreParents;
        }

        return FocusZoneKeyStroke.IgnoreNone;
    };
}
